import { APIError, apiClient } from '@/utils/api-client';

interface MagicLinkResponse {
  success: boolean;
  message: string;
  email: string;
  expires_in_minutes?: number;
  magic_link?: string; // Only present in development mode
}

interface VerifyResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    display_name: string;
    roles: Array<{
      id: string;
      name: string;
      display_name: string;
    }>;
  };
}

interface RefreshResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

interface DirectLoginResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    display_name: string;
    roles: Array<{
      id: string;
      name: string;
      display_name: string;
    }>;
  };
}

class AuthService {
  async sendMagicLink(
    email: string,
    next?: string
  ): Promise<MagicLinkResponse> {
    return apiClient.post('/auth/send-magic-link', { email, next });
  }

  async verifyMagicLink(token: string): Promise<VerifyResponse> {
    return apiClient.post<VerifyResponse>(
      '/auth/verify',
      { token },
      { credentials: 'include' } // Include cookies for refresh token
    );
  }

  async verifyOTP(email: string, code: string): Promise<VerifyResponse> {
    return apiClient.post<VerifyResponse>(
      '/auth/verify-otp',
      { email, code },
      { credentials: 'include' }
    );
  }

  async refreshToken(): Promise<RefreshResponse> {
    try {
      return await apiClient.post<RefreshResponse>(
        '/auth/refresh',
        undefined,
        { credentials: 'include' } // Include cookies for refresh token
      );
    } catch (error) {
      // Handle 401 - refresh token is invalid or expired. api-client throws
      // APIError('Unauthorized', 401): the message is 'Unauthorized' (no '401'
      // substring), so match on the typed status, not the message text.
      if (error instanceof APIError && error.status === 401) {
        return { access_token: '', token_type: '', expires_in: 0 };
      }
      throw error;
    }
  }

  async getCurrentUser() {
    return apiClient.get('/auth/me');
  }

  async logout() {
    await apiClient.post('/auth/logout', undefined, { credentials: 'include' });
  }

  // Development only - direct login for API testing
  async directLogin(email: string): Promise<DirectLoginResponse> {
    if (!import.meta.env.DEV) {
      throw new Error('Direct login is only available in development mode');
    }

    const data = await apiClient.post<DirectLoginResponse>(
      '/auth/test/direct-login',
      { email }
    );

    // Store the access token
    if (data.access_token) {
      localStorage.setItem('access_token', data.access_token);
    }

    return data;
  }
}

export const authService = new AuthService();
